'use client';

import { useUser } from '@clerk/nextjs';
import {
	ArrowRight,
	ArrowUp,
	Brush,
	Camera,
	CheckCircle,
	ChevronDown,
	Layers3,
	Loader2,
	Menu,
	Shirt,
	Sparkles,
	Users2,
	X,
} from 'lucide-react';
import dynamic from 'next/dynamic';
import Image from '@/components/ui/CspImage';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PLAN_IDS, PLANS } from '@/lib/plans';
import WebinarBanner from '@/components/WebinarBanner';
import { isWebinarLive } from '@/lib/webinar';

const BlogSection = dynamic(() => import('./BlogSection'));
const NewsletterSignup = dynamic(
	() => import('../newsletter/NewsletterSignup'),
	{ ssr: false },
);
// Server-rendered on purpose: with `ssr: false` the globe button mounted after
// hydration and pushed "Sign In" + the nav links 40px left on every hard load
// (openspec change nav-language-switcher-ssr). The chunk is still code-split.
const LanguageSwitcher = dynamic(() => import('../LanguageSwitcher'));

import { useMutation, useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';

interface LandingPageClientProps {
	initialWaitlistMode: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	initialFeaturedPosts?: Record<string, any>[];
}

// Waitlist form data interface
interface WaitlistFormData {
	name: string;
	jobTitle: string;
	company: string;
	email: string;
	interestedFeature: string;
	interestedFeatureOther: string;
	biggestChallenge: string;
	featureRequest: string;
}

const getFeatureOptions = (t: ReturnType<typeof useTranslations>) => [
	{
		id: 'concept-creator',
		label: t('waitlist.features.conceptCreator.label'),
		description: t('waitlist.features.conceptCreator.description'),
		icon: Brush,
	},
	{
		id: 'mix-match',
		label: t('waitlist.features.mixMatch.label'),
		description: t('waitlist.features.mixMatch.description'),
		icon: Shirt,
	},
	{
		id: 'photoshooting',
		label: t('waitlist.features.photoshooting.label'),
		description: t('waitlist.features.photoshooting.description'),
		icon: Camera,
	},
	{
		id: 'other',
		label: t('waitlist.features.other.label'),
		description: t('waitlist.features.other.description'),
		icon: Sparkles,
	},
];

export default function LandingPageClient({
	initialWaitlistMode,
	initialFeaturedPosts,
}: LandingPageClientProps) {
	const { isSignedIn } = useUser();
	// Pricing reflects admin-edited DB config (falls back to code defaults).
	const planCfg = useQuery(api.planConfig.getPlanConfig, {});
	const plansEff = (planCfg?.plans ?? PLANS) as typeof PLANS;
	const router = useRouter();
	const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

	// Waitlist mode - use the server-fetched initial value
	const waitlistModeEnabled = initialWaitlistMode;
	const addToWaitlist = useMutation(api.waitlist.addToWaitlist);
	const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);
	const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
	const [waitlistSuccess, setWaitlistSuccess] = useState(false);
	const [waitlistError, setWaitlistError] = useState('');
	const [formData, setFormData] = useState<WaitlistFormData>({
		name: '',
		jobTitle: '',
		company: '',
		email: '',
		interestedFeature: '',
		interestedFeatureOther: '',
		biggestChallenge: '',
		featureRequest: '',
	});
	const [showFeedbackSection, setShowFeedbackSection] = useState(false);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [showFloatingCTA, setShowFloatingCTA] = useState(false);
	const [showBackToTop, setShowBackToTop] = useState(false);
	const [showAllFaqs, setShowAllFaqs] = useState(false);
	const [activeTestimonial, setActiveTestimonial] = useState(0);
	const [activeSection, setActiveSection] = useState('');
	const webinarLive = isWebinarLive();
	const [scrollProgress, setScrollProgress] = useState(0);
	const [touchStart, setTouchStart] = useState<number | null>(null);
	const [touchEnd, setTouchEnd] = useState<number | null>(null);
	const [isTestimonialPaused, setIsTestimonialPaused] = useState(false);
	const [pricingPeriod, setPricingPeriod] = useState<'monthly' | 'yearly'>(
		'monthly',
	);
	const t = useTranslations();
	const FEATURE_OPTIONS = getFeatureOptions(t);
	const heroTitleLine1 = t('hero.titleLine1');
	const heroTitleLine2 = t('hero.titleLine2');

	// Scroll handler for floating CTA, back to top, progress bar, and active section
	// Throttled with requestAnimationFrame to prevent layout thrashing
	const rafRef = useRef<number>(0);
	useEffect(() => {
		const handleScroll = () => {
			if (rafRef.current) return;
			rafRef.current = requestAnimationFrame(() => {
				const scrollY = window.scrollY;
				setShowBackToTop(scrollY > 1200);

				// Calculate scroll progress
				const docHeight =
					document.documentElement.scrollHeight - window.innerHeight;
				const progress = docHeight > 0 ? (scrollY / docHeight) * 100 : 0;
				setScrollProgress(progress);

				// Check if user is in pricing or try-it section to hide floating CTA
				const pricingSection = document.getElementById('pricing');
				const tryItSection = document.getElementById('try-it');
				let isInOverlap = false;
				if (pricingSection) {
					const rect = pricingSection.getBoundingClientRect();
					const isInPricing = rect.top < window.innerHeight && rect.bottom > 0;
					isInOverlap = isInPricing;
				}
				if (tryItSection) {
					const rect = tryItSection.getBoundingClientRect();
					if (rect.top < window.innerHeight && rect.bottom > 0)
						isInOverlap = true;
				}
				setShowFloatingCTA(scrollY > 600 && !isInOverlap);

				// Determine active section for nav highlighting
				const sections = ['tools', 'use-cases', 'pricing', 'blog'];
				let currentSection = '';
				for (const sectionId of sections) {
					const element = document.getElementById(sectionId);
					if (element) {
						const rect = element.getBoundingClientRect();
						if (rect.top <= 150 && rect.bottom >= 150) {
							currentSection = sectionId;
							break;
						}
					}
				}
				setActiveSection(currentSection);
				rafRef.current = 0;
			});
		};
		window.addEventListener('scroll', handleScroll, { passive: true });
		return () => {
			window.removeEventListener('scroll', handleScroll);
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
		};
	}, []);

	// Auto-rotate testimonials on mobile (pause on hover/touch) — deferred
	useEffect(() => {
		if (isTestimonialPaused) return;
		let interval: ReturnType<typeof setInterval>;
		const start = () => {
			interval = setInterval(() => {
				setActiveTestimonial((prev) => (prev + 1) % 3);
			}, 5000);
		};
		if (typeof requestIdleCallback !== 'undefined') {
			const id = requestIdleCallback(start);
			return () => {
				cancelIdleCallback(id);
				clearInterval(interval);
			};
		}
		const timer = setTimeout(start, 2000);
		return () => {
			clearTimeout(timer);
			clearInterval(interval);
		};
	}, [isTestimonialPaused]);

	// Touch swipe handlers for carousels
	const minSwipeDistance = 50;
	const handleTouchStart = (e: React.TouchEvent) => {
		setTouchEnd(null);
		setTouchStart(e.targetTouches[0].clientX);
	};
	const handleTouchMove = (e: React.TouchEvent) => {
		setTouchEnd(e.targetTouches[0].clientX);
	};
	const handleTestimonialSwipe = () => {
		if (!touchStart || !touchEnd) return;
		const distance = touchStart - touchEnd;
		if (Math.abs(distance) >= minSwipeDistance) {
			if (distance > 0) {
				setActiveTestimonial((prev) => (prev + 1) % 3);
			} else {
				setActiveTestimonial((prev) => (prev - 1 + 3) % 3);
			}
		}
	};
	// Mobile menu body scroll lock
	useEffect(() => {
		if (isMobileMenuOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [isMobileMenuOpen]);

	// Scroll-triggered animations — deferred to avoid blocking initial paint
	useEffect(() => {
		const setup = () => {
			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							entry.target.classList.add('is-visible');
							observer.unobserve(entry.target); // Stop observing once visible
						}
					});
				},
				{ threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
			);

			const elements = document.querySelectorAll('.scroll-animate');
			elements.forEach((el) => {
				observer.observe(el);
			});

			return () => observer.disconnect();
		};

		// Defer observer setup to avoid competing with LCP
		if (typeof requestIdleCallback !== 'undefined') {
			const id = requestIdleCallback(() => {
				setup();
			});
			return () => cancelIdleCallback(id);
		}
		const timer = setTimeout(setup, 100);
		return () => clearTimeout(timer);
	}, []);

	// Smooth scroll function with header offset
	const scrollToSection = (
		e: React.MouseEvent<HTMLAnchorElement>,
		href: string,
	) => {
		e.preventDefault();
		const targetId = href.replace('#', '');
		const element = document.getElementById(targetId);
		if (element) {
			const headerOffset = 80; // Fixed header height
			const elementPosition = element.getBoundingClientRect().top;
			const offsetPosition =
				elementPosition + window.pageYOffset - headerOffset;
			window.scrollTo({
				top: offsetPosition,
				behavior: 'smooth',
			});
		}
		setIsMobileMenuOpen(false);
	};

	const scrollToTop = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	// Signed-in users are NOT auto-redirected away from the landing page — they
	// can browse the marketing site freely. Navigation to /dashboard only happens
	// on explicit actions (e.g. the "Get started" button).

	const handleGetStarted = useCallback(() => {
		if (isSignedIn) {
			router.push('/dashboard');
		} else if (waitlistModeEnabled) {
			// Open waitlist modal instead of sign-in
			setIsWaitlistModalOpen(true);
			setWaitlistSuccess(false);
			setWaitlistError('');
			setShowFeedbackSection(false);
			setFormData({
				name: '',
				jobTitle: '',
				company: '',
				email: '',
				interestedFeature: '',
				interestedFeatureOther: '',
				biggestChallenge: '',
				featureRequest: '',
			});
		} else {
			router.push('/sign-in');
		}
	}, [isSignedIn, waitlistModeEnabled, router]);

	// Paid-plan pricing CTA: carry the chosen plan through sign-up so the user
	// lands in the purchase modal on that plan (fixes "clicked Pro trial, got
	// free plan"). Waitlist mode falls back to the waitlist flow.
	const handlePlanCta = useCallback(
		(planId: string) => {
			if (!isSignedIn && waitlistModeEnabled) {
				handleGetStarted();
				return;
			}
			const target = `/dashboard?upgrade=${planId}&period=${pricingPeriod}`;
			if (isSignedIn) {
				router.push(target);
			} else {
				router.push(`/sign-in?redirect_url=${encodeURIComponent(target)}`);
			}
		},
		[isSignedIn, waitlistModeEnabled, pricingPeriod, router, handleGetStarted],
	);

	const handleWaitlistSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Validate required fields
		if (!formData.name.trim()) {
			setWaitlistError(t('waitlist.errorName'));
			return;
		}
		if (!formData.jobTitle.trim()) {
			setWaitlistError(t('waitlist.errorJobTitle'));
			return;
		}
		if (!formData.company.trim()) {
			setWaitlistError(t('waitlist.errorCompany'));
			return;
		}
		if (!formData.email.trim()) {
			setWaitlistError(t('waitlist.errorEmail'));
			return;
		}
		if (!formData.interestedFeature) {
			setWaitlistError(t('waitlist.errorFeature'));
			return;
		}
		if (
			formData.interestedFeature === 'other' &&
			!formData.interestedFeatureOther.trim()
		) {
			setWaitlistError(t('waitlist.errorFeatureOther'));
			return;
		}

		setWaitlistSubmitting(true);
		setWaitlistError('');

		try {
			await addToWaitlist({
				email: formData.email.trim(),
				name: formData.name.trim(),
				jobTitle: formData.jobTitle.trim(),
				company: formData.company.trim(),
				interestedFeature: formData.interestedFeature,
				interestedFeatureOther:
					formData.interestedFeatureOther.trim() || undefined,
				biggestChallenge: formData.biggestChallenge.trim() || undefined,
				featureRequest: formData.featureRequest.trim() || undefined,
				source: 'ces-2026',
			});
			setWaitlistSuccess(true);
		} catch (error: any) {
			setWaitlistError(error.message || t('waitlist.errorFallback'));
		} finally {
			setWaitlistSubmitting(false);
		}
	};

	const updateFormData = (field: keyof WaitlistFormData, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (waitlistError) setWaitlistError('');
	};

	const desktopNavLinkClass =
		'font-body whitespace-nowrap text-[14px] leading-none font-normal text-[#383838] transition-colors duration-200 hover:text-[#383838]/80';
	const primaryHeroButtonClass =
		'inline-flex h-[59.96px] items-center justify-center gap-2 rounded-[12.84px] bg-primary px-[31.96px] py-[17.98px] font-body text-[24px] font-medium text-white leading-none transition-colors duration-200 hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';
	const secondaryHeroButtonClass =
		'inline-flex h-[59.93px] items-center justify-center rounded-[12.84px] border border-[#F6542E] px-[31.96px] py-[15.98px] font-body text-[24px] font-normal text-[#F6542E] leading-none transition-colors duration-200 hover:bg-[#F6542E]/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F6542E] focus-visible:ring-offset-2';

	return (
		<div className="landing-theme min-h-screen overflow-x-hidden bg-background font-body">
			{/* Skip to main content link for keyboard navigation */}
			<a
				href="#main-content"
				className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:rounded-lg focus:bg-primary focus:px-4 focus:py-2 focus:text-white"
			>
				{t('nav.skipToMain')}
			</a>

			{/* Webinar promo banner (auto-hides after the event) */}
			{webinarLive && <WebinarBanner source="landing" />}

			{/* Scroll Progress Bar */}
			<div className={`fixed right-0 left-0 z-[60] h-0.5 bg-transparent ${webinarLive ? 'top-10' : 'top-0'}`}>
				<div
					className="h-full w-0 bg-primary transition-all duration-150 ease-out"
					ref={element => { if (element) element.style.width = `${scrollProgress}%` }}
				/>
			</div>

			{/* Navigation */}
			<nav
				aria-label="Main navigation"
				className={`fixed z-50 w-full bg-background/95 backdrop-blur-md ${webinarLive ? 'top-[42px]' : 'top-0.5'}`}
			>
				<div className="mx-auto max-w-[1920px] px-6 py-4 sm:px-10 lg:px-[120px]">
					<div className="flex items-center justify-between">
						<a
							href="/"
							className="flex items-center"
							aria-label="StyTrix Home"
						>
							{/* Light mode logo */}
							<Image
								src="/StyTrix_Logo_RGB_Orange.png"
								alt="StyTrix"
								width={324}
								height={86}
								className="block dark:hidden w-auto h-[68px]"
								priority
							/>
							{/* Dark mode logo */}
							<Image
								src="/StyTrix_Logo_RGB_Orange.png"
								alt="StyTrix"
								width={324}
								height={86}
								className="hidden dark:block w-auto h-[68px]"
							/>
						</a>

						{/* Navigation Links - Desktop */}
						<div className="hidden lg:flex flex-1 items-center justify-end gap-4 pl-8">
							<div className="flex items-center gap-8 xl:gap-10">
								<a
										href="/documentation"
										className={desktopNavLinkClass}
									>
										{t('nav.documentation')}
									</a>
								<a
									href="#pricing"
									onClick={(e) => scrollToSection(e, '#pricing')}
									className={desktopNavLinkClass}
								>
									{t('nav.pricing')}
								</a>
								<a
										href="/blog"
										className={desktopNavLinkClass}
									>
										{t('nav.blog')}
									</a>
							</div>

							<div className="flex items-center gap-2">
								<button
									onClick={handleGetStarted}
									className="hover:-translate-y-0.5 rounded-[12px] bg-foreground px-[20px] py-[10px] font-body text-[14px] font-semibold text-background transition-all duration-300 hover:bg-primary hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
									aria-label={
										t('hero.ctaAriaStart')
									}
								>
									Sign In
								</button>
								<LanguageSwitcher variant="icon" />
							</div>
						</div>

						{/* Navigation Links - Tablet (compact) */}
						<div className="hidden items-center gap-2 md:flex lg:hidden">
							<a
								href="/documentation"
								className="rounded-lg px-2.5 py-1.5 font-medium text-[13px] text-muted-foreground transition-all duration-200 hover:bg-muted/50 hover:text-foreground"
							>
								{t('nav.documentation')}
							</a>
							<a
								href="#pricing"
								onClick={(e) => scrollToSection(e, '#pricing')}
								className={`rounded-lg px-2.5 py-1.5 font-medium text-[13px] transition-all duration-200 ${activeSection === 'pricing' ? 'bg-primary/5 text-primary' : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'}`}
							>
								{t('nav.pricing')}
							</a>
						</div>

						<div className="flex items-center gap-3 lg:hidden">
							<button
								onClick={handleGetStarted}
								className="hover:-translate-y-0.5 hidden rounded-[12px] bg-foreground px-[24px] py-[12px] font-semibold text-background transition-all duration-300 hover:bg-primary hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:block"
								aria-label={
									t('hero.ctaAriaStart')
								}
							>
								Sign In
							</button>

							{/* Mobile Menu Button */}
							<button
								onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
								className="rounded-lg p-2.5 transition-colors hover:bg-secondary md:hidden"
								aria-label={t('aria.toggleMenu')}
								aria-expanded={isMobileMenuOpen}
							>
								{isMobileMenuOpen ? (
									<X className="h-6 w-6" />
								) : (
									<Menu className="h-6 w-6" />
								)}
							</button>
						</div>
					</div>
				</div>

				{/* Mobile Menu */}
				<div
					className={`absolute top-full right-0 left-0 border-border border-b bg-background/95 shadow-elevated-lg backdrop-blur-lg transition-all duration-300 md:hidden ${
						isMobileMenuOpen
							? 'translate-y-0 opacity-100'
							: '-translate-y-4 pointer-events-none opacity-0'
					}`}
				>
					<div className="space-y-4 px-6 py-6">
						<a
								href="/documentation"
								className="-mx-3 block rounded-lg border-border/50 border-b px-3 py-3 font-medium text-foreground text-lg transition-all duration-200 hover:bg-secondary/50 hover:text-primary"
							>
								{t('nav.documentation')}
							</a>
						<a
							href="#pricing"
							onClick={(e) => scrollToSection(e, '#pricing')}
							className="-mx-3 block rounded-lg border-border/50 border-b px-3 py-3 font-medium text-foreground text-lg transition-all duration-200 hover:bg-secondary/50 hover:text-primary"
						>
							{t('nav.pricing')}
						</a>
						<a
								href="/blog"
								className="-mx-3 block rounded-lg border-border/50 border-b px-3 py-3 font-medium text-foreground text-lg transition-all duration-200 hover:bg-secondary/50 hover:text-primary"
							>
								{t('nav.blog')}
							</a>
						<button
							onClick={() => {
								handleGetStarted();
								setIsMobileMenuOpen(false);
							}}
							className="mt-4 w-full rounded-lg bg-foreground px-6 py-3 font-semibold text-background transition-all duration-200 hover:bg-primary hover:shadow-lg hover:shadow-primary/25 active:scale-[0.98]"
						>
							Sign In
						</button>
						<div className="flex items-center justify-center border-border border-t pt-4">
							<LanguageSwitcher variant="icon" />
						</div>
					</div>
				</div>
			</nav>

			{/* Everything between the nav and the footer is the page's main content.
			    Without this landmark the skip link had no target and every section
			    sat outside a landmark region for screen-reader users. */}
			<main id="main-content">

			{/* Hero Section */}
			<section
				className={`relative overflow-hidden bg-background ${webinarLive ? 'mt-10' : ''}`}
				aria-labelledby="hero-heading"
			>
				<div className="mx-auto px-6 pt-28 pb-20 sm:px-10 sm:pt-32 lg:px-0 lg:pt-28 min-[1536px]:pt-6">
					{/* --stx-hero-scale is set before first paint (app/layout.tsx +
					    globals.css), so server and client markup are identical and the
					    hero no longer snaps from scale(1) after hydration. */}
					<div 
						className="mx-auto hidden lg:block w-full max-w-[1920px] overflow-visible h-[calc(1080px_*_var(--stx-hero-scale,1))]"
					>
						<div 
							className="relative w-[1920px] h-[1080px] origin-top-left [transform:scale(var(--stx-hero-scale,1))]"
						>
							<div className="absolute top-[370.22px] left-[119.79px] z-20 flex w-[950.82px] flex-col gap-[41.09px]">
								<div className="flex flex-col gap-[25.68px]">
									<h1
										id="hero-heading"
										className="font-display font-normal text-[#383838] text-[72px] leading-[1.2] tracking-[0]"
									>
										{heroTitleLine1}
										{heroTitleLine2 ? (
											<>
												<br />
												{heroTitleLine2}
											</>
										) : null}
									</h1>
									<p className="w-[630px] font-body font-normal text-[#383838] text-[24px] leading-[1.3] tracking-[0]">
										{t('hero.subtitle')}
									</p>
								</div>

								<div className="flex items-center gap-[15.98px]">
									<button
										onClick={handleGetStarted}
										className={primaryHeroButtonClass}
										aria-label={
											waitlistModeEnabled && !isSignedIn
												? t('hero.ctaAriaWaitlist')
												: t('hero.ctaAriaStart')
										}
									>
										<span>{t('hero.ctaStartCreating')}</span>
										<span aria-hidden="true">→</span>
									</button>
									<a
										href="/tools"
										className={secondaryHeroButtonClass}
										aria-label={t('hero.ctaAriaExplore')}
									>
										{t('hero.ctaExploreTools')}
									</a>
								</div>
							</div>

							<div className="absolute top-[54.44px] left-[1205.89px] h-[1187.59px] w-[789.28px]">
								<div
									className="absolute inset-0"
									aria-hidden="true"
								>
									<Image
										src="/landing/stytrix-refresh/ascii-black.png"
										alt=""
										fill
										className="scale-x-[-1] object-fill"
										sizes="789px"
										priority
									/>
								</div>

								<div className="absolute top-[150.95px] left-[83.95px] h-[438.13px] w-[303.9px] overflow-hidden">
									<Image
										src="/landing/stytrix-refresh/frame-with-garment.png"
										alt="AI-generated garment frame"
										fill
										priority
										className="object-cover object-center"
										sizes="304px"
									/>
								</div>

								<div
									className="absolute top-[593.8px] left-[541.2px] h-[192.65px] w-[260.99px] rotate-180 overflow-hidden opacity-95 mix-blend-multiply"
									aria-hidden="true"
								>
									<Image
										src="/landing/stytrix-refresh/graphic-01.png"
										alt=""
										fill
										className="object-cover"
										sizes="261px"
									/>
								</div>

								<div
									className="absolute top-[756.6px] left-[257.93px] h-[286.24px] w-[262.53px] overflow-hidden opacity-95 mix-blend-multiply"
									aria-hidden="true"
								>
									<Image
										src="/landing/stytrix-refresh/graphic-02.png"
										alt=""
										fill
										className="object-contain"
										sizes="263px"
									/>
								</div>
							</div>
						</div>
					</div>

					<div className="mx-auto max-w-[760px] lg:hidden">
						<div className="text-center">
							<h1
								id="hero-heading-mobile"
								className="pt-4 font-display font-normal text-[#383838] text-[clamp(2.75rem,10vw,4.25rem)] leading-[1.08] tracking-[0]"
							>
								{heroTitleLine1}
								{heroTitleLine2 ? (
									<>
										<br />
										{heroTitleLine2}
									</>
								) : null}
							</h1>
							<p className="mx-auto mt-5 max-w-[34rem] font-body text-[#383838] text-[18px] leading-[1.35] sm:text-[20px]">
								{t('hero.subtitle')}
							</p>
							<div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center">
								<button
									onClick={handleGetStarted}
									className="inline-flex h-14 items-center justify-center gap-2 rounded-xl bg-primary px-8 font-body text-[24px] font-medium text-white"
									aria-label={
										waitlistModeEnabled && !isSignedIn
											? t('hero.ctaAriaWaitlist')
											: t('hero.ctaAriaStart')
									}
								>
									<span>{t('hero.ctaStartCreating')}</span>
									<span aria-hidden="true">→</span>
								</button>
								<a
									href="/tools"
									className="inline-flex h-14 items-center justify-center rounded-xl border border-[#F6542E] px-8 font-body text-[24px] font-normal text-[#F6542E]"
									aria-label={t('hero.ctaAriaExplore')}
								>
									{t('hero.ctaExploreTools')}
								</a>
							</div>
						</div>

						<div className="relative mx-auto mt-10 h-[520px] w-full max-w-[380px] overflow-hidden">
							<div
								className="absolute top-0 right-0 h-[520px] w-[346px]"
								aria-hidden="true"
							>
								<Image
									src="/landing/stytrix-refresh/ascii-black.png"
									alt=""
									fill
									className="scale-x-[-1] object-contain object-top opacity-90"
									sizes="346px"
								/>
							</div>
							<div className="absolute top-[52px] left-[34px] h-[280px] w-[194px] overflow-hidden">
								<Image
									src="/landing/stytrix-refresh/frame-with-garment.png"
									alt="AI-generated garment frame"
									fill
									priority
									className="object-cover object-center"
									sizes="194px"
								/>
							</div>
							<div
								className="absolute top-[262px] right-[12px] h-[124px] w-[168px] rotate-180 overflow-hidden opacity-95 mix-blend-multiply"
								aria-hidden="true"
							>
								<Image
									src="/landing/stytrix-refresh/graphic-01.png"
									alt=""
									fill
									className="object-cover"
									sizes="168px"
								/>
							</div>
							<div
								className="absolute bottom-[18px] left-[86px] h-[184px] w-[170px] overflow-hidden opacity-95 mix-blend-multiply"
								aria-hidden="true"
							>
								<Image
									src="/landing/stytrix-refresh/graphic-02.png"
									alt=""
									fill
									className="object-contain"
									sizes="170px"
								/>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Value Proposition - PDF Canvas Showcase */}
			<section className="relative overflow-hidden bg-background px-6 pt-6 pb-16 sm:px-10 lg:px-0 lg:pt-0 lg:pb-20">
				<div className="mx-auto max-w-[1280px]">
					<div className="mx-auto flex max-w-[800px] flex-col items-center text-center">
						<h2 className="font-display text-[56px] font-normal leading-[114%] tracking-[0] text-[#383838]">
							{t('valueProp.title').split('. ').map((part, i, arr) => (
								<span
									// biome-ignore lint/suspicious/noArrayIndexKey: static i18n title split into sentence fragments
									key={i}
								>
									{part}{i < arr.length - 1 ? '.' : ''}
									{i === 0 && <br className="hidden sm:block" />}
									{i > 0 && i < arr.length - 1 && ' '}
								</span>
							))}
						</h2>
						<p className="mx-auto mt-3 max-w-[750px] font-body text-[17px] font-normal leading-[130%] tracking-[0.02em] text-[#383838]/70">
							{t('valueProp.subtitle')}
						</p>

						<div
							className="relative mx-auto mt-5 w-full max-w-[801.44px]"
						>
							<div className="relative overflow-hidden rounded-[10.1px] shadow-[0_18px_40px_rgba(42,38,35,0.16)]">
								<Image
									src="/landing/generated/showcase-canvas-designs-v3.webp"
									alt={t('valueProp.canvasAlt')}
									width={1920}
									height={940}
									className="h-auto w-full"
									loading="lazy"
									sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 704px"
								/>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Use Cases Gallery - Product Demo Video */}
			<section
				id="use-cases"
				className="cv-auto overflow-hidden bg-gradient-to-b from-secondary via-background to-background px-6 py-24"
			>
				<div className="mx-auto max-w-7xl">
					{/* Section Header */}
					<div className="mb-12 text-center">
						<div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2">
							<Layers3 className="h-4 w-4 text-primary" />
							<span className="font-medium text-primary text-sm tracking-wide">
								{t('useCases.sectionBadge')}
							</span>
						</div>
						<h2 className="font-display text-[56px] font-normal leading-[114%] tracking-[0] text-[#383838]">
							See StyTrix in Action
						</h2>
					</div>

					{/* Product demo video */}
					<div className="mx-auto max-w-5xl overflow-hidden rounded-2xl shadow-[0_18px_40px_rgba(42,38,35,0.16)]">
						<video
							src="/landing/generated/about-stytrix-v2.mp4"
							width={1920}
							height={1080}
							autoPlay
							loop
							muted
							playsInline
							preload="metadata"
							className="h-auto w-full"
							aria-label={t('useCases.sectionBadge')}
						/>
					</div>
				</div>
			</section>

			{/* Real-time Collaboration Showcase */}
			<section className="px-6 py-20">
				<div className="mx-auto max-w-6xl">
					<div className="mb-16 text-center">
						<h2 className="mb-6 font-display text-[56px] font-normal leading-[114%] tracking-[0] text-[#221E1C]">
							{t('valueProp.collabTitle')}
						</h2>
						<p className="mx-auto max-w-3xl text-muted-foreground text-xl">
							{t('valueProp.collabDescription')}
						</p>
					</div>

					<div className="grid gap-8 md:grid-cols-2">
						{/* Team Working Image */}
						<div className="group relative aspect-video overflow-hidden rounded-2xl border border-border shadow-xl transition-all duration-300 hover:shadow-2xl">
							<Image
								src="/landing/generated/collab-team-working-v2.webp"
								alt={t('valueProp.collabTeamAlt')}
								width={1280}
								height={720}
								className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
							/>
						</div>

						{/* Collaboration Video */}
						<div className="group relative aspect-video overflow-hidden rounded-2xl border border-border shadow-xl transition-all duration-300 hover:shadow-2xl">
							<video
								src="/landing/generated/collaboration.mp4"
								width={1280}
								height={720}
								autoPlay
								loop
								muted
								playsInline
								preload="metadata"
								aria-label={t('valueProp.collabFeedbackAlt')}
								className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
							/>
						</div>
					</div>

					<div className="mx-auto mt-12 grid max-w-2xl grid-cols-3 gap-8 text-center">
						<div className="group rounded-xl p-4 transition-all duration-300 hover:bg-secondary/50">
							<div className="mb-2 font-bold text-3xl text-primary transition-transform duration-300 group-hover:scale-110">
								∞
							</div>
							<div className="text-muted-foreground text-sm">
								{t('valueProp.teamMembers')}
							</div>
						</div>
						<div className="group rounded-xl p-4 transition-all duration-300 hover:bg-secondary/50">
							<div className="mb-2 font-bold text-3xl text-primary transition-transform duration-300 group-hover:scale-110">
								&lt;50ms
							</div>
							<div className="text-muted-foreground text-sm">
								{t('valueProp.syncLatency')}
							</div>
						</div>
						<div className="group rounded-xl p-4 transition-all duration-300 hover:bg-secondary/50">
							<div className="mb-2 font-bold text-3xl text-primary transition-transform duration-300 group-hover:scale-110">
								24/7
							</div>
							<div className="text-muted-foreground text-sm">
								{t('valueProp.alwaysAvailable')}
							</div>
						</div>
					</div>

					{/* Invite Your Team CTA - Advocacy hook */}
					<div className="mt-10 text-center">
						<button
							onClick={handleGetStarted}
							className="group inline-flex items-center gap-2 rounded-lg border-2 border-primary/30 bg-card px-6 py-3 font-medium text-primary transition-all duration-300 hover:border-primary hover:bg-primary hover:text-white"
						>
							<Users2
								className="h-4 w-4"
								aria-hidden="true"
							/>
							{t('valueProp.inviteTeamCta')}
							<ArrowRight
								className="h-4 w-4 transition-transform group-hover:translate-x-1"
								aria-hidden="true"
							/>
						</button>
					</div>
				</div>
			</section>

			{/* Testimonials - Editorial Magazine Style */}
			<section className="relative overflow-hidden bg-background px-6 py-24">
				{/* Decorative Elements */}
				<div className="-translate-x-1/2 -translate-y-1/2 absolute top-0 left-0 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
				<div className="absolute right-0 bottom-0 h-64 w-64 translate-x-1/2 translate-y-1/2 rounded-full bg-primary/5 blur-3xl" />

				<div className="relative mx-auto max-w-6xl">
					{/* Section Header */}
					<div className="scroll-animate mb-16 text-center">
						<div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2">
							<Sparkles className="h-4 w-4 text-primary" />
							<span className="font-medium text-primary text-sm tracking-wide">
								{t('testimonials.sectionBadge')}
							</span>
						</div>
						<h2 className="mb-6 whitespace-nowrap font-display text-[56px] font-normal leading-[114%] tracking-[0] text-[#221E1C]">
							{t('testimonials.sectionTitle')}
						</h2>
						<p className="mx-auto max-w-2xl text-muted-foreground text-xl">
							{t('testimonials.sectionSubtitle')}
						</p>
					</div>

					{/* Testimonials - Desktop Grid / Mobile Carousel */}
					{(() => {
						const testimonials = [
							{
								name: 'Laure Mimram',
								role: t('testimonials.sarah.role'),
								company: t('testimonials.sarah.company'),
								image: '/landing/testimonials/testimonial-laure.webp',
								quote: t('testimonials.sarah.quote'),
								accent: 'bg-gradient-to-br from-primary/20 to-primary/5',
							},
							{
								name: 'Marcel Kędziora',
								role: t('testimonials.marcus.role'),
								company: t('testimonials.marcus.company'),
								image: '/landing/testimonials/testimonial-marcel.webp',
								quote: t('testimonials.marcus.quote'),
								accent: 'bg-gradient-to-br from-blue-500/20 to-blue-500/5',
								featured: true,
							},
							{
								name: 'Iris Lu',
								role: t('testimonials.yuki.role'),
								company: t('testimonials.yuki.company'),
								image: '/landing/testimonials/testimonial-iris.webp',
								quote: t('testimonials.yuki.quote'),
								accent:
									'bg-gradient-to-br from-emerald-500/20 to-emerald-500/5',
							},
						];

						return (
							<>
								{/* Desktop Grid */}
								<div className="hidden gap-8 md:grid md:grid-cols-3">
									{testimonials.map((testimonial, i) => (
										<div
											// biome-ignore lint/suspicious/noArrayIndexKey: static testimonials list from i18n
											key={i}
											className={`group relative rounded-2xl p-8 transition-all duration-500 ${
												testimonial.featured
													? 'md:-mt-4 bg-primary text-white shadow-xl hover:shadow-2xl md:mb-4'
													: 'hover:-translate-y-1 border border-border bg-card hover:border-primary/30 hover:shadow-elevated-lg'
											}`}
										>
											{/* Large Quote Mark */}
											<div
												className={`absolute top-6 right-6 font-serif text-8xl leading-none ${
													testimonial.featured
														? 'text-white/10'
														: 'text-primary/10'
												}`}
											>
												"
											</div>

											{/* Quote */}
											<blockquote
												className={`relative z-10 mb-8 text-lg leading-relaxed ${
													testimonial.featured
														? 'text-white/90'
														: 'text-muted-foreground'
												}`}
											>
												"{testimonial.quote}"
											</blockquote>

											{/* Author */}
											<div className="relative z-10 flex items-center gap-4">
												<div className="relative">
													<div
														className={`h-14 w-14 overflow-hidden rounded-full ring-2 transition-all duration-300 group-hover:ring-4 ${
															testimonial.featured
																? 'ring-white/30 group-hover:ring-white/50'
																: 'ring-primary/20 group-hover:ring-primary/40'
														}`}
													>
														<Image
															src={testimonial.image}
															alt={testimonial.name}
															width={56}
															height={56}
															className="h-full w-full object-cover"
														/>
													</div>
													{/* Online indicator */}
													<div
														className={`-bottom-0.5 -right-0.5 absolute h-4 w-4 rounded-full border-2 ${
															testimonial.featured
																? 'border-primary bg-green-400'
																: 'border-card bg-green-500'
														}`}
													/>
												</div>
												<div>
													<div
														className={`font-semibold ${testimonial.featured ? 'text-white' : 'text-foreground'}`}
													>
														{testimonial.name}
													</div>
													<div
														className={`text-sm ${testimonial.featured ? 'text-white/90' : 'text-muted-foreground'}`}
													>
														{testimonial.role}
													</div>
													<div
														className={`font-medium text-xs ${testimonial.featured ? 'text-white/90' : 'text-primary'}`}
													>
														{testimonial.company}
													</div>
												</div>
											</div>

											{/* Hover accent line */}
											<div
												className={`absolute right-0 bottom-0 left-0 h-1 origin-left scale-x-0 rounded-b-2xl transition-transform duration-500 group-hover:scale-x-100 ${
													testimonial.featured ? 'bg-white/30' : 'bg-primary'
												}`}
											/>
										</div>
									))}
								</div>

								{/* Mobile Carousel */}
								<div
									className="md:hidden"
									onMouseEnter={() => setIsTestimonialPaused(true)}
									onMouseLeave={() => setIsTestimonialPaused(false)}
								>
									<div
										className="relative overflow-hidden"
										onTouchStart={handleTouchStart}
										onTouchMove={handleTouchMove}
										onTouchEnd={handleTestimonialSwipe}
									>
										<div
											className="flex transition-transform duration-500 ease-out"
											ref={element => { if (element) element.style.transform = `translateX(-${activeTestimonial * 100}%)` }}
										>
											{testimonials.map((testimonial, i) => (
												<div
													// biome-ignore lint/suspicious/noArrayIndexKey: static testimonials list from i18n
													key={i}
													className="w-full flex-shrink-0 px-2"
												>
													<div
														className={`relative rounded-2xl p-6 ${
															testimonial.featured
																? 'bg-primary text-white shadow-xl'
																: 'border border-border bg-card'
														}`}
													>
														{/* Large Quote Mark */}
														<div
															className={`absolute top-4 right-4 font-serif text-6xl leading-none ${
																testimonial.featured
																	? 'text-white/10'
																	: 'text-primary/10'
															}`}
														>
															"
														</div>

														{/* Quote */}
														<blockquote
															className={`relative z-10 mb-6 text-base leading-relaxed ${
																testimonial.featured
																	? 'text-white/90'
																	: 'text-muted-foreground'
															}`}
														>
															"{testimonial.quote}"
														</blockquote>

														{/* Author */}
														<div className="relative z-10 flex items-center gap-3">
															<div
																className={`h-12 w-12 overflow-hidden rounded-full ring-2 ${
																	testimonial.featured
																		? 'ring-white/30'
																		: 'ring-primary/20'
																}`}
															>
																<Image
																	src={testimonial.image}
																	alt={testimonial.name}
																	width={48}
																	height={48}
																	className="h-full w-full object-cover"
																/>
															</div>
															<div>
																<div
																	className={`font-semibold text-sm ${testimonial.featured ? 'text-white' : 'text-foreground'}`}
																>
																	{testimonial.name}
																</div>
																<div
																	className={`text-xs ${testimonial.featured ? 'text-white/90' : 'text-muted-foreground'}`}
																>
																	{testimonial.role}
																</div>
															</div>
														</div>
													</div>
												</div>
											))}
										</div>
									</div>

									{/* Mobile Carousel Dots */}
									<div className="mt-6 flex justify-center gap-2">
										{testimonials.map((_, i) => (
											<button
												// biome-ignore lint/suspicious/noArrayIndexKey: carousel dots — the index IS the slide number
												key={i}
												onClick={() => setActiveTestimonial(i)}
												className={`h-2 rounded-full transition-all duration-300 ${
													activeTestimonial === i
														? 'w-6 bg-primary'
														: 'w-2 bg-border hover:bg-primary/30'
												}`}
												aria-label={t('aria.goToTestimonial', { index: i + 1 })}
											/>
										))}
									</div>
								</div>
							</>
						);
					})()}
				</div>
			</section>

			{/* Pricing Plans */}
			<section
				id="pricing"
				className="cv-auto bg-secondary px-6 py-10"
			>
				<div className="mx-auto max-w-[1180px]">
					<div className="mb-5 text-center">
						<h2 className="font-display text-[56px] font-normal leading-[47.94px] tracking-[-0.96px] text-[#221E1C]">
							{t('pricing.sectionTitle')}
						</h2>

						<div className="mt-4 inline-flex items-center gap-2 rounded-full border border-border bg-card p-1.5 shadow-sm">
							<button
								onClick={() => setPricingPeriod('monthly')}
								className={`rounded-full px-5 py-2.5 font-medium text-[20px] transition-all duration-300 ${
									pricingPeriod === 'monthly'
										? 'bg-primary text-white shadow-md'
										: 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
								}`}
							>
								{t('pricing.monthly')}
							</button>
							<button
								onClick={() => setPricingPeriod('yearly')}
								className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-medium text-[20px] transition-all duration-300 ${
									pricingPeriod === 'yearly'
										? 'bg-foreground text-background shadow-md'
										: 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
								}`}
							>
								{t('pricing.yearly')}
								<span
									className={`rounded-full px-2 py-0.5 text-xs ${pricingPeriod === 'yearly' ? 'bg-primary text-white' : 'bg-primary/10 text-primary'}`}
								>
									{t('pricing.yearlyDiscount')}
								</span>
							</button>
						</div>
					</div>

					<div className="mx-auto grid max-w-[1180px] items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_1.7fr]">
						{PLAN_IDS.filter((planId) => planId !== 'free' && planId !== 'education').map((planId) => {
							const plan = plansEff[planId]
							const featured = planId === 'pro'
							// 數字一律來自 catalog（lib/plans.ts），避免 i18n 出現第二套定價
							const priceDisplay = plan.custom
								? t('pricing.tiers.enterprise.priceLabel')
								: pricingPeriod === 'yearly' && plan.annualTotal
									? `$${Math.round(plan.annualTotal / 12)}`
									: `$${plan.monthlyFee}`
							const features = t.raw(`pricing.tiers.${planId}.features`) as string[]
							return (
							<div
								key={planId}
								className={`hover:-translate-y-1 relative flex flex-col rounded-2xl bg-card p-5 shadow-sm transition-all duration-300 hover:shadow-xl ${
									featured
										? 'border-2 border-primary'
										: 'border border-border hover:border-primary/40'
								}`}
							>
								{featured && (
									<div className="-top-3 absolute left-6 rounded-full bg-primary px-4 py-1 font-bold text-white text-xs uppercase tracking-[0.16em] shadow-lg shadow-primary/20">
										{t('pricing.popular')}
									</div>
								)}

								<h3 className="mb-3 font-body text-[22px] font-normal leading-[33px] tracking-[0.26px] text-[#221E1C]">
									{t(`pricing.tiers.${planId}.name`)}
								</h3>
								<p className="min-h-[3rem] text-muted-foreground text-sm leading-relaxed">
									{t(`pricing.tiers.${planId}.tagline`)}
								</p>

								<div className="mt-5 border-border border-y py-4">
									<div className="flex items-end gap-2">
										<span className="font-display text-[40px] font-normal leading-[40px] text-[#221E1C]">
											{priceDisplay}
										</span>
										{!plan.custom && (
											<span className="pb-1 font-body text-[15px] font-light text-[#221E1C]/80 leading-[23px]">
												{t('pricing.perMonth')}
											</span>
										)}
									</div>
									{pricingPeriod === 'yearly' && !plan.custom && plan.annualTotal ? (
										<div className="mt-2 font-medium text-primary text-sm">
											{t('pricing.billedYearly', { total: plan.annualTotal })}{plan.annualSavings ? ` · save $${plan.annualSavings}` : ''}
										</div>
									) : null}

									{plan.effectivePrice ? (
										<div className="mt-3 text-left text-[#221E1C]/75 text-xs">
											≈ ${plan.effectivePrice.toFixed(2)} / credit · {plan.creditsMonthly?.toLocaleString()} credits/mo
										</div>
									) : (
										<div className="mt-3 text-left text-xs invisible" aria-hidden="true">
											placeholder
										</div>
									)}
									{!plan.custom ? (
										<div className="mt-2 text-left font-bold text-[11px] text-primary uppercase tracking-[0.08em]">
											{t('pricing.trialBadge')}
										</div>
									) : (
										<div className="mt-2 text-left text-[#221E1C]/75 text-xs">
											{t('pricing.tiers.enterprise.customNote')}
										</div>
									)}
								</div>

								<ul
									className={
										planId === 'enterprise'
											? 'mt-4 grid flex-grow grid-cols-2 gap-x-4 gap-y-2'
											: 'mt-4 flex-grow space-y-2.5'
									}
								>
									{features.map((feature) => (
										<li
											key={feature}
											className="flex items-start gap-3"
										>
											<span className="mt-1 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-primary/15">
												<span className="h-2 w-2 rounded-full bg-primary" />
											</span>
											<span className="font-body text-[15px] font-light leading-[22px] text-[#221E1C]/80">
												{feature}
											</span>
										</li>
									))}
								</ul>

								<button
									onClick={
										plan.custom
											? () =>
													window.open(
														'https://cal.com/stytrix/b2b',
														'_blank',
														'noopener,noreferrer',
													)
											: () => handlePlanCta(planId)
									}
									className={`mt-5 w-full rounded-xl px-5 py-3 font-semibold text-base transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
										featured
											? 'bg-primary text-white hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/20'
											: 'border-2 border-[#221E1C] bg-card text-[#221E1C] hover:border-primary hover:bg-primary hover:text-white'
									}`}
								>
									{plan.custom
										? t('pricing.contactSales')
										: t('pricing.getStarted')}
								</button>
							</div>
							)
						})}
					</div>

				</div>
			</section>

			{/* FAQ Section */}
			<section className="px-6 py-20">
				<div className="mx-auto max-w-3xl">
					<div className="scroll-animate mb-16 text-center">
						<h2 className="mb-6 font-display text-[56px] font-normal leading-[114%] tracking-[0] text-[#221E1C]">
							{t('faq.sectionTitle')}
						</h2>
						<p className="text-muted-foreground text-xl">
							{t('faq.sectionSubtitle')}
						</p>
					</div>

					<div className="space-y-4">
						{(t.raw('faq.items') as Array<{ question: string; answer: string }>)
							.slice(0, showAllFaqs ? undefined : 5)
							.map((faq, index) => (
								<div
									// biome-ignore lint/suspicious/noArrayIndexKey: static i18n FAQ list; the index doubles as the open-accordion state id
									key={index}
									className={`overflow-hidden rounded-xl border bg-card transition-all duration-300 ${openFaqIndex === index ? 'border-primary/30 shadow-elevated' : 'border-border hover:border-border/80'}`}
								>
									<h3>
										<button
											onClick={() =>
												setOpenFaqIndex(openFaqIndex === index ? null : index)
											}
											className="flex w-full items-center justify-between px-6 py-5 text-left transition-colors hover:bg-secondary/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
											id={`faq-question-${index}`}
											aria-expanded={openFaqIndex === index}
											aria-controls={`faq-answer-${index}`}
										>
											<span className="pr-4 font-semibold text-foreground text-lg">
												{faq.question}
											</span>
											<ChevronDown
												className={`h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform duration-300 ${
													openFaqIndex === index ? 'rotate-180' : ''
												}`}
												aria-hidden="true"
											/>
										</button>
									</h3>
									<div
										id={`faq-answer-${index}`}
										role="region"
										aria-labelledby={`faq-question-${index}`}
										className={`transition-all duration-300 ease-in-out motion-reduce:transition-none ${
											openFaqIndex === index
												? 'max-h-96 opacity-100'
												: 'max-h-0 opacity-0'
										} overflow-hidden`}
									>
										<div className="px-6 pb-5 text-muted-foreground leading-relaxed">
											{faq.answer}
										</div>
									</div>
								</div>
							))}
					</div>

					{/* Show More Button */}
					{!showAllFaqs && (
						<div className="mt-8 text-center">
							<button
								onClick={() => setShowAllFaqs(true)}
								className="inline-flex items-center gap-2 rounded-lg px-6 py-3 font-medium text-primary transition-colors hover:bg-primary/10"
							>
								{t('faq.showAll')}
								<ChevronDown className="h-4 w-4" />
							</button>
						</div>
					)}
				</div>
			</section>

			{/* Blog Section */}
			<BlogSection initialPosts={initialFeaturedPosts} />

			{/* Newsletter Signup */}
			<NewsletterSignup
				variant="inline"
				source="landing"
			/>

			</main>
			{/* Footer */}
			<footer className="border-border border-t bg-card">
				<div className="mx-auto max-w-[1920px] px-6 py-16 sm:px-10 lg:px-[120px]">
					{/* Main Footer Content */}
					<div className="mb-12 grid grid-cols-2 gap-8 md:grid-cols-4">
						{/* Brand Column */}
						<div className="col-span-2 md:col-span-1">
							<div className="-mt-5 mb-4">
								{/* Light mode logo */}
								<Image
									src="/StyTrix_Logo_RGB_Orange.png"
									alt="StyTrix"
									width={250}
									height={66}
									className="block dark:hidden w-auto h-[66px]"
								/>
								{/* Dark mode logo */}
								<Image
									src="/StyTrix_Logo_RGB_Orange.png"
									alt="StyTrix"
									width={250}
									height={66}
									className="hidden dark:block w-auto h-[66px]"
								/>
							</div>
							<p className="mb-4 text-muted-foreground text-sm">
								{t('footer.brandDescription')}
							</p>
							{/* Social Links */}
							<div className="flex gap-3">
								<a
									href="https://linkedin.com/company/stytrix"
									target="_blank"
									rel="noopener noreferrer nofollow"
									className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary transition-all duration-200 hover:scale-110 hover:bg-primary hover:text-white"
									aria-label="LinkedIn"
								>
									<svg
										className="h-4 w-4"
										fill="currentColor"
										viewBox="0 0 24 24"
									>
										<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
									</svg>
								</a>
								<a
									href="https://www.instagram.com/stytrix.ai/"
									target="_blank"
									rel="noopener noreferrer nofollow"
									className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary transition-all duration-200 hover:scale-110 hover:bg-primary hover:text-white"
									aria-label="Instagram"
								>
									<svg
										className="h-4 w-4"
										fill="currentColor"
										viewBox="0 0 24 24"
									>
										<path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
									</svg>
								</a>
							</div>
						</div>

						{/* Product Column */}
						<div>
							<h4 className="mb-4 font-semibold text-foreground">
								{t('footer.product')}
							</h4>
							<ul className="space-y-3">
								<li>
									<a
										href="/tools"
										className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{t('nav.features')}
									</a>
								</li>
								<li>
									<a
										href="/tools"
										className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										All Tools
									</a>
								</li>
								<li>
									<a
										href="#use-cases"
										onClick={(e) => scrollToSection(e, '#use-cases')}
										className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{t('nav.useCases')}
									</a>
								</li>
								<li>
									<a
										href="#pricing"
										onClick={(e) => scrollToSection(e, '#pricing')}
										className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{t('nav.pricing')}
									</a>
								</li>
								<li>
									<a
										href="#blog"
										onClick={(e) => scrollToSection(e, '#blog')}
										className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{t('nav.blog')}
									</a>
								</li>
								<li>
									<a
										href="/guide"
										className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{t('footer.guide')}
									</a>
								</li>
							</ul>
						</div>

						{/* Company Column */}
						<div>
							<h4 className="mb-4 font-semibold text-foreground">
								{t('footer.company')}
							</h4>
							<ul className="space-y-3">
								<li>
									<a
										href="/about"
										className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{t('footer.about')}
									</a>
								</li>
								<li>
									<a
										href="/careers"
										className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{t('footer.careers')}
									</a>
								</li>
								<li>
									<a
										href="/contact"
										className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{t('footer.contact')}
									</a>
								</li>
								<li>
									<a
										href="/press"
										className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{t('footer.press')}
									</a>
								</li>
							</ul>
						</div>

						{/* Legal Column */}
						<div>
							<h4 className="mb-4 font-semibold text-foreground">
								{t('footer.legal')}
							</h4>
							<ul className="space-y-3">
								<li>
									<a
										href="/privacy"
										className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{t('footer.privacy')}
									</a>
								</li>
								<li>
									<a
										href="/subprocessors"
										className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{t('footer.subprocessors')}
									</a>
								</li>
								<li>
									<a
										href="/terms"
										className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{t('footer.terms')}
									</a>
								</li>
								<li>
									<a
										href="/cookies"
										className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{t('footer.cookies')}
									</a>
								</li>
								<li>
									<a
										href="/accessibility"
										className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary"
									>
										{t('footer.accessibility')}
									</a>
								</li>
							</ul>
						</div>
					</div>

					{/* Trust Badges & Copyright */}
					<div className="border-border border-t pt-8">
						<div className="flex flex-col items-center justify-between gap-4 md:flex-row">
							{/* Trust Badges */}
							<div className="flex items-center gap-4 text-muted-foreground text-xs">
								<div className="flex items-center gap-1.5">
									<svg
										className="h-4 w-4 text-green-500"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
										/>
									</svg>
									<span>{t('footer.sslSecure')}</span>
								</div>
								<div className="flex items-center gap-1.5">
									<svg
										className="h-4 w-4 text-blue-500"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									</svg>
									<span>{t('footer.gdprCompliant')}</span>
								</div>
							</div>

							{/* Copyright */}
							<div className="text-muted-foreground text-sm">
								{t('footer.copyright', { year: new Date().getFullYear() })}
							</div>
						</div>
					</div>
				</div>
			</footer>

			{/* Floating CTA Button - Mobile */}
			<div
				className={`fixed right-4 bottom-6 left-4 z-40 transition-all duration-300 md:hidden ${
					showFloatingCTA
						? 'translate-y-0 opacity-100'
						: 'pointer-events-none translate-y-8 opacity-0'
				}`}
			>
				<button
					onClick={handleGetStarted}
					className="hover:-translate-y-0.5 flex w-full items-center justify-center gap-2 rounded-xl bg-primary/95 px-6 py-4 font-semibold text-lg text-white shadow-primary/30 shadow-xl backdrop-blur-sm transition-all duration-200 hover:bg-primary hover:shadow-2xl active:translate-y-0"
				>
					{waitlistModeEnabled && !isSignedIn
						? t('cta.floatingButtonWaitlist')
						: t('cta.floatingButton')}
					<ArrowRight className="h-5 w-5" />
				</button>
			</div>

			{/* Back to Top Button */}
			<button
				onClick={scrollToTop}
				className={`hover:-translate-y-0.5 fixed right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full border border-border bg-card/95 shadow-lg backdrop-blur-sm transition-all duration-300 hover:border-primary/30 hover:bg-secondary hover:shadow-elevated active:translate-y-0 ${
					showFloatingCTA ? 'bottom-40 md:bottom-24' : 'bottom-24'
				} ${
					showBackToTop
						? 'translate-y-0 opacity-100'
						: 'pointer-events-none translate-y-4 opacity-0'
				}`}
				aria-label={t('aria.backToTop')}
			>
				<ArrowUp className="h-5 w-5 text-foreground" />
			</button>

			{/* Waitlist Modal - Editorial Fashion Form */}
			{isWaitlistModalOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm">
					<div
						className="relative my-8 w-full max-w-2xl"
						style={{
							animation:
								'modalSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards',
						}}
					>
						{/* Decorative elements */}
						<div className="-top-4 -left-4 pointer-events-none absolute h-24 w-24 border-primary/20 border-t-2 border-l-2" />
						<div className="-bottom-4 -right-4 pointer-events-none absolute h-24 w-24 border-primary/20 border-r-2 border-b-2" />

						<div className="overflow-hidden rounded-sm bg-card shadow-2xl">
							{/* Header with white background and primary accent */}
							<div className="relative overflow-hidden border-border/50 border-b bg-card px-8 py-8">
								{/* Subtle pattern overlay */}
								<div
									className="absolute inset-0 opacity-5"
									style={{
										backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%234f46e5' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
									}}
								/>

								{/* Close button */}
								<button
									onClick={() => setIsWaitlistModalOpen(false)}
									className="absolute top-4 right-4 rounded-full p-2 text-muted-foreground/70 transition-colors hover:bg-secondary hover:text-muted-foreground"
									aria-label={t('aria.closeModal')}
								>
									<X className="h-5 w-5" />
								</button>

								<div className="relative">
									<h2
										className="mb-3 font-light text-2xl text-foreground tracking-tight md:text-3xl"
										style={{
											fontFamily: 'system-ui, -apple-system, sans-serif',
										}}
									>
										{t('waitlist.title')}
									</h2>
									<p className="max-w-lg text-muted-foreground text-sm leading-relaxed">
										{t('waitlist.subtitle')}
									</p>
								</div>
							</div>

							{waitlistSuccess ? (
								// Success state with elegant animation
								<div className="px-8 py-16 text-center">
									<div
										className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 shadow-emerald-200 shadow-lg"
										style={{
											animation:
												'successPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
										}}
									>
										<CheckCircle className="h-10 w-10 text-white" />
									</div>
									<h3 className="mb-3 font-light text-2xl text-foreground tracking-tight">
										{t('waitlist.successTitle')}
									</h3>
									<p className="mx-auto mb-8 max-w-md text-muted-foreground">
										{t('waitlist.successMessage')}
									</p>
									<button
										onClick={() => setIsWaitlistModalOpen(false)}
										className="bg-primary px-8 py-3 font-medium text-sm text-white uppercase tracking-wide transition-colors hover:bg-primary/90"
									>
										{t('waitlist.close')}
									</button>
								</div>
							) : (
								// Form with sophisticated styling
								<form
									onSubmit={handleWaitlistSubmit}
									className="px-8 py-6"
								>
									<div className="space-y-6">
										{/* Row 1: Name & Job Title */}
										<div className="grid gap-6 md:grid-cols-2">
											<div className="space-y-2">
												<label
													htmlFor="name"
													className="block font-medium text-muted-foreground text-xs uppercase tracking-wider"
												>
													{t('waitlist.nameLabel')}{' '}
													<span className="text-primary">*</span>
												</label>
												<input
													id="name"
													type="text"
													value={formData.name}
													onChange={(e) =>
														updateFormData('name', e.target.value)
													}
													placeholder={t('waitlist.namePlaceholder')}
													autoComplete="name"
													className="w-full border-0 border-border border-b-2 bg-transparent px-0 py-3 text-foreground transition-colors placeholder:text-muted-foreground/50 focus:border-primary focus:ring-0"
													disabled={waitlistSubmitting}
												/>
											</div>
											<div className="space-y-2">
												<label
													htmlFor="jobTitle"
													className="block font-medium text-muted-foreground text-xs uppercase tracking-wider"
												>
													{t('waitlist.jobTitleLabel')}{' '}
													<span className="text-primary">*</span>
												</label>
												<input
													id="jobTitle"
													type="text"
													value={formData.jobTitle}
													onChange={(e) =>
														updateFormData('jobTitle', e.target.value)
													}
													placeholder={t('waitlist.jobTitlePlaceholder')}
													autoComplete="organization-title"
													className="w-full border-0 border-border border-b-2 bg-transparent px-0 py-3 text-foreground transition-colors placeholder:text-muted-foreground/50 focus:border-primary focus:ring-0"
													disabled={waitlistSubmitting}
												/>
											</div>
										</div>

										{/* Row 2: Company & Email */}
										<div className="grid gap-6 md:grid-cols-2">
											<div className="space-y-2">
												<label
													htmlFor="company"
													className="block font-medium text-muted-foreground text-xs uppercase tracking-wider"
												>
													{t('waitlist.companyLabel')}{' '}
													<span className="text-primary">*</span>
												</label>
												<input
													id="company"
													type="text"
													value={formData.company}
													onChange={(e) =>
														updateFormData('company', e.target.value)
													}
													placeholder={t('waitlist.companyPlaceholder')}
													autoComplete="organization"
													className="w-full border-0 border-border border-b-2 bg-transparent px-0 py-3 text-foreground transition-colors placeholder:text-muted-foreground/50 focus:border-primary focus:ring-0"
													disabled={waitlistSubmitting}
												/>
											</div>
											<div className="space-y-2">
												<label
													htmlFor="email"
													className="block font-medium text-muted-foreground text-xs uppercase tracking-wider"
												>
													{t('waitlist.emailLabel')}{' '}
													<span className="text-primary">*</span>
												</label>
												<input
													id="email"
													type="email"
													value={formData.email}
													onChange={(e) =>
														updateFormData('email', e.target.value)
													}
													placeholder={t('waitlist.emailPlaceholder')}
													autoComplete="email"
													className="w-full border-0 border-border border-b-2 bg-transparent px-0 py-3 text-foreground transition-colors placeholder:text-muted-foreground/50 focus:border-primary focus:ring-0"
													disabled={waitlistSubmitting}
												/>
											</div>
										</div>

										{/* Feature Interest Selection - Compact Radio Buttons */}
										<div className="space-y-3 pt-2">
											<label className="block font-medium text-muted-foreground text-xs uppercase tracking-wider">
												{t('waitlist.featureLabel')}{' '}
												<span className="text-primary">*</span>
											</label>
											<div className="flex flex-wrap gap-2">
												{FEATURE_OPTIONS.map((option) => {
													const isSelected =
														formData.interestedFeature === option.id;
													return (
														<label
															key={option.id}
															className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-sm transition-all ${
																isSelected
																	? 'border-primary bg-primary/10 font-medium text-primary'
																	: 'border-border bg-secondary text-muted-foreground hover:border-border hover:bg-secondary'
															}`}
														>
															<input
																type="radio"
																name="interestedFeature"
																value={option.id}
																checked={isSelected}
																onChange={(e) =>
																	updateFormData(
																		'interestedFeature',
																		e.target.value,
																	)
																}
																className="sr-only"
																disabled={waitlistSubmitting}
															/>
															{isSelected && (
																<span className="h-1.5 w-1.5 rounded-full bg-primary/100" />
															)}
															{option.label}
														</label>
													);
												})}
											</div>

											{/* Other feature input */}
											{formData.interestedFeature === 'other' && (
												<input
													type="text"
													value={formData.interestedFeatureOther}
													onChange={(e) =>
														updateFormData(
															'interestedFeatureOther',
															e.target.value,
														)
													}
													placeholder={t('waitlist.featureOtherPlaceholder')}
													className="w-full rounded-lg border border-border bg-card px-4 py-2.5 text-foreground text-sm transition-colors placeholder:text-muted-foreground/70 focus:border-primary focus:ring-1 focus:ring-primary"
													disabled={waitlistSubmitting}
												/>
											)}
										</div>

										{/* Collapsible Optional Feedback Section */}
										<div className="pt-2">
											<button
												type="button"
												onClick={() =>
													setShowFeedbackSection(!showFeedbackSection)
												}
												className="flex items-center gap-2 text-muted-foreground text-sm transition-colors hover:text-primary"
											>
												<ChevronDown
													className={`h-4 w-4 transition-transform ${showFeedbackSection ? 'rotate-180' : ''}`}
												/>
												{showFeedbackSection
													? t('waitlist.hideFeedback')
													: t('waitlist.shareFeedback')}
											</button>

											{showFeedbackSection && (
												<div className="mt-4 grid gap-4 border-border/50 border-t pt-4 md:grid-cols-2">
													<div className="space-y-2">
														<label
															htmlFor="biggestChallenge"
															className="block font-medium text-muted-foreground text-xs uppercase tracking-wider"
														>
															{t('waitlist.challengeLabel')}
														</label>
														<textarea
															id="biggestChallenge"
															value={formData.biggestChallenge}
															onChange={(e) =>
																updateFormData(
																	'biggestChallenge',
																	e.target.value,
																)
															}
															placeholder={t('waitlist.challengePlaceholder')}
															rows={2}
															className="w-full resize-none rounded-lg border border-border bg-secondary px-3 py-2 text-foreground text-sm transition-all placeholder:text-muted-foreground/70 focus:border-primary focus:bg-card focus:ring-1 focus:ring-primary"
															disabled={waitlistSubmitting}
														/>
													</div>
													<div className="space-y-2">
														<label
															htmlFor="featureRequest"
															className="block font-medium text-muted-foreground text-xs uppercase tracking-wider"
														>
															{t('waitlist.featureRequestLabel')}
														</label>
														<textarea
															id="featureRequest"
															value={formData.featureRequest}
															onChange={(e) =>
																updateFormData('featureRequest', e.target.value)
															}
															placeholder={t(
																'waitlist.featureRequestPlaceholder',
															)}
															rows={2}
															className="w-full resize-none rounded-lg border border-border bg-secondary px-3 py-2 text-foreground text-sm transition-all placeholder:text-muted-foreground/70 focus:border-primary focus:bg-card focus:ring-1 focus:ring-primary"
															disabled={waitlistSubmitting}
														/>
													</div>
												</div>
											)}
										</div>

										{/* Error Message */}
										{waitlistError && (
											<div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
												<X className="h-4 w-4 flex-shrink-0" />
												{waitlistError}
											</div>
										)}

										{/* Submit Button */}
										<div className="pt-4">
											<button
												type="submit"
												disabled={waitlistSubmitting}
												className="group hover:-translate-y-0.5 w-full rounded-lg bg-primary px-8 py-3.5 font-medium text-sm text-white uppercase tracking-wide shadow-sm transition-all duration-200 hover:bg-primary/90 hover:shadow-elevated active:translate-y-0 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
											>
												<span className="flex items-center justify-center gap-3">
													{waitlistSubmitting ? (
														<>
															<Loader2 className="h-5 w-5 animate-spin" />
															{t('waitlist.submitting')}
														</>
													) : (
														<>
															{t('waitlist.submitButton')}
															<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
														</>
													)}
												</span>
											</button>
										</div>
									</div>

									{/* Privacy note */}
									<p className="mt-6 text-center text-muted-foreground/70 text-xs">
										{t('waitlist.privacyNote')}
									</p>
								</form>
							)}
						</div>
					</div>
				</div>
			)}

			{/* Custom animations */}
			<style
				jsx
				global
			>{`
        /* === COLLABORATION CURSOR ANIMATIONS === */

        /* Sarah's cursor - diagonal movement with pauses */
        @keyframes cursorSarah {
          0%, 10% {
            transform: translate(0, 0);
          }
          15% {
            transform: translate(40px, 20px);
          }
          25%, 35% {
            transform: translate(80px, 60px);
          }
          40% {
            transform: translate(120px, 40px);
          }
          50%, 60% {
            transform: translate(60px, 80px);
          }
          70% {
            transform: translate(20px, 50px);
          }
          85%, 100% {
            transform: translate(0, 0);
          }
        }
        .animate-cursor-sarah {
          animation: cursorSarah 12s ease-in-out infinite;
        }

        /* Marcus's cursor - horizontal sweep */
        @keyframes cursorMarcus {
          0%, 15% {
            transform: translate(0, 0);
          }
          25% {
            transform: translate(-60px, 10px);
          }
          35%, 45% {
            transform: translate(-100px, 30px);
          }
          55% {
            transform: translate(-40px, -20px);
          }
          70%, 80% {
            transform: translate(30px, 10px);
          }
          90%, 100% {
            transform: translate(0, 0);
          }
        }
        .animate-cursor-marcus {
          animation: cursorMarcus 14s ease-in-out infinite;
          animation-delay: 2s;
        }

        /* Yuki's cursor - vertical movement */
        @keyframes cursorYuki {
          0%, 20% {
            transform: translate(0, 0);
          }
          30% {
            transform: translate(-20px, 60px);
          }
          45%, 55% {
            transform: translate(10px, 100px);
          }
          65% {
            transform: translate(-30px, 50px);
          }
          80%, 100% {
            transform: translate(0, 0);
          }
        }
        .animate-cursor-yuki {
          animation: cursorYuki 11s ease-in-out infinite;
          animation-delay: 4s;
        }

        /* Name tag fade in */
        @keyframes fadeInTag {
          0% {
            opacity: 0;
            transform: translateX(-5px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-fade-in-tag {
          animation: fadeInTag 0.3s ease-out forwards;
        }

        /* Cursor click ripple effect */
        @keyframes cursorClick {
          0%, 45%, 100% {
            opacity: 0;
            transform: scale(0.5);
          }
          50% {
            opacity: 1;
            transform: scale(0.8);
          }
          70% {
            opacity: 0.5;
            transform: scale(1.2);
          }
          85% {
            opacity: 0;
            transform: scale(1.5);
          }
        }
        .animate-cursor-click {
          animation: cursorClick 12s ease-out infinite;
        }

        /* Selection pulse for Marcus */
        @keyframes selectionPulse {
          0%, 30%, 100% {
            opacity: 0;
          }
          35%, 50% {
            opacity: 1;
          }
          55% {
            opacity: 0;
          }
        }
        .animate-selection-pulse {
          animation: selectionPulse 14s ease-in-out infinite;
          animation-delay: 2s;
        }

        /* Typing indicator for Yuki */
        @keyframes typingIndicator {
          0%, 40%, 100% {
            opacity: 0;
            transform: scale(0.8);
          }
          45%, 70% {
            opacity: 1;
            transform: scale(1);
          }
          75% {
            opacity: 0;
            transform: scale(0.8);
          }
        }
        .animate-typing-indicator {
          animation: typingIndicator 11s ease-in-out infinite;
          animation-delay: 4s;
        }

        /* Individual typing dots */
        @keyframes typingDot {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
        }
        .animate-typing-dot {
          animation: typingDot 0.6s ease-in-out infinite;
        }

        /* Comment bubble appearance */
        @keyframes commentAppear {
          0%, 20%, 100% {
            opacity: 0;
            transform: translateY(10px) scale(0.9);
          }
          25%, 45% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          50% {
            opacity: 0;
            transform: translateY(-5px) scale(0.95);
          }
        }
        .animate-comment-appear > div {
          animation: commentAppear 12s ease-out infinite;
        }

        /* Design highlight effect */
        @keyframes designHighlight {
          0%, 32%, 100% {
            opacity: 0;
            transform: scale(0.95);
          }
          37%, 52% {
            opacity: 1;
            transform: scale(1);
          }
          57% {
            opacity: 0;
            transform: scale(1.02);
          }
        }
        .animate-design-highlight > div {
          animation: designHighlight 14s ease-in-out infinite;
          animation-delay: 2s;
        }

        /* === SCROLL-TRIGGERED ANIMATIONS === */
        .scroll-animate {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        .scroll-animate.is-visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* === CTA PULSE ANIMATION === */
        @keyframes ctaPulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(148, 40, 71, 0.4);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(148, 40, 71, 0);
          }
        }
        .animate-cta-pulse {
          animation: ctaPulse 2.5s ease-in-out infinite;
        }

        /* === MODAL ANIMATIONS === */
        @keyframes modalSlideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes successPop {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes zoomIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        /* === REDUCED MOTION === */
        @media (prefers-reduced-motion: reduce) {
          @keyframes modalSlideUp {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes successPop {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes zoomIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .animate-breathing,
          .animate-breathing-pulse,
          .animate-pulse,
          .animate-cursor-sarah,
          .animate-cursor-marcus,
          .animate-cursor-yuki,
          .animate-cursor-click,
          .animate-selection-pulse,
          .animate-typing-indicator,
          .animate-typing-dot,
          .animate-comment-appear > div,
          .animate-design-highlight > div,
          .animate-cta-pulse {
            animation: none !important;
          }
          /* Disable scroll animations for reduced motion */
          .scroll-animate {
            opacity: 1 !important;
            transform: none !important;
          }
          /* Disable hover transforms for reduced motion */
          * {
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
		</div>
	);
}
